import { useState } from 'react';
import { Button, Col, DatePicker, Row, Select } from 'antd';
import type { SelectValue } from 'antd/lib/select';
import dayjs from 'dayjs';

import { UserOrganization, UserRole } from '../../../../enum';
import { isMobile } from '../../../../helpers/utils.helper';
import { useAppSelector } from '../../../../hooks';
import { getDateRangeValue } from '../../report/report.helper';
import { datesRange, getAdminSiteFilter } from '../program-dashboard.helper';
import type { DashboardRequestParams } from '../programDashboardSlice';
import { InfoPopover } from './InfoPopover';

export interface ProgramDashboardFilterFormProps {
  onFinish: () => void;
  submitLoading: boolean;
  programDashboardParams: DashboardRequestParams;
  onParamsChange: (params: Partial<DashboardRequestParams>) => void;
  isOlhDashboard?: boolean;
}

export const ProgramDashboardFilterForm = ({
  onFinish,
  submitLoading,
  programDashboardParams,
  onParamsChange,
  isOlhDashboard,
}: ProgramDashboardFilterFormProps) => {
  const currentUser = useAppSelector((state) => state.authSlice.currentUser);
  const adminSites = useAppSelector((state) => state.adminSitesSlice.adminSites).filter(
    getAdminSiteFilter(currentUser, programDashboardParams.organization_id),
  );
  const clinicianSites = useAppSelector((state) => state.adminClinicianSlice.clinicianSites);
  const adminOrganizationsData = useAppSelector((state) => state.adminOrganizationSlice.adminOrganizationsData);
  const isAdmin = currentUser.role === UserRole.ADMIN;
  const [dateRangeSelected, setDateRangeSelected] = useState<SelectValue>(
    getDateRangeValue(dayjs(programDashboardParams.start_date), dayjs(programDashboardParams.end_date), datesRange),
  );

  return (
    <>
      <Row>
        {isAdmin && (
          <Col>
            <Row>
              <p>Organization</p>
            </Row>
            <Row>
              <Select
                style={{ width: 300, marginRight: 24, marginBottom: isMobile ? 8 : 0 }}
                popupMatchSelectWidth={false}
                showSearch={{ optionFilterProp: 'label' }}
                allowClear={true}
                placeholder="Select an organization"
                value={programDashboardParams.organization_id ?? ''}
                disabled={isOlhDashboard}
                onChange={(value) => onParamsChange({ organization_id: value })}
                options={[
                  { value: '', label: 'Select all organizations' },
                  ...(adminOrganizationsData
                    ?.filter((orgs) => isOlhDashboard || orgs.id !== UserOrganization.OLH)
                    .map((orgs) => ({ value: orgs.id, label: orgs.name })) ?? []),
                ]}
              />
            </Row>
          </Col>
        )}
        <Col>
          <Row style={{ marginLeft: 10 }}>
            <p>Date range</p>
          </Row>
          <Row>
            <Select
              style={{ width: 120, marginRight: 16, marginBottom: isMobile ? 8 : 0, marginLeft: 10 }}
              popupMatchSelectWidth={false}
              showSearch
              value={dateRangeSelected}
              onChange={(value: SelectValue) => {
                setDateRangeSelected(value);
                const [amount, unit] = (value as string).split(' ');
                onParamsChange({
                  start_date: dayjs().subtract(Number(amount), unit as dayjs.ManipulateType),
                  end_date: dayjs(),
                });
              }}
              options={datesRange}
            />
            <DatePicker.RangePicker
              style={{ marginRight: 24 }}
              disabled={[false, false]}
              allowClear={false}
              value={[programDashboardParams.start_date, programDashboardParams.end_date]}
              disabledDate={(currentDate) => currentDate.isAfter(dayjs())}
              onChange={(dates) => {
                if (dates) {
                  const [startDate, endDate] = dates;
                  if (startDate && endDate) {
                    setDateRangeSelected(getDateRangeValue(startDate, endDate, datesRange));
                    onParamsChange({
                      start_date: startDate,
                      end_date: endDate,
                    });
                  }
                }
              }}
            />
          </Row>
        </Col>
        <Col style={isMobile ? { marginTop: 8 } : undefined}>
          <Row>
            {isOlhDashboard ? (
              <InfoPopover
                info
                title={
                  <span className="infoTitle">
                    <p>Sites</p>
                  </span>
                }
                content={
                  <>
                    <p>Select a site to see data for all clients assigned to that site.</p>
                    <p>
                      Note that clients can belong to multiple sites. Data on all of their check-ins will be represented
                      in the dashboard, even if some of those check-ins were at other sites.
                    </p>
                  </>
                }
              />
            ) : (
              <p>Site</p>
            )}
          </Row>
          <Row>
            <Select
              style={{ width: 250, marginRight: 24 }}
              popupMatchSelectWidth={false}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              showSearch={{ optionFilterProp: 'label' }}
              allowClear={currentUser.role !== UserRole.SITE && Boolean(programDashboardParams.site_id)}
              value={programDashboardParams.site_id ?? ''}
              placeholder="Select a site"
              onChange={(value) => onParamsChange({ site_id: value })}
              options={
                currentUser.role === UserRole.ADMIN ||
                currentUser.role === UserRole.ORGANIZATION ||
                currentUser.role === UserRole.PROGRAM_ADMINISTRATION
                  ? [
                      { value: '', label: 'Select all sites' },
                      ...(adminSites?.map((site) => ({ value: site.id, label: site.name })) ?? []),
                    ]
                  : (clinicianSites?.map((site) => ({ value: site.site_id, label: site.name })) ?? [])
              }
            />
          </Row>
        </Col>
        <Col style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <Row>
            <Button onClick={onFinish} loading={submitLoading} type="primary">
              Submit
            </Button>
          </Row>
        </Col>
      </Row>
    </>
  );
};
